import { Canvas } from "@react-three/fiber";
import { Float, Stars } from "@react-three/drei";
import { useEffect, useRef, useState } from "react";
import { useQueryStates, parseAsString, parseAsStringEnum } from "nuqs";
import gsap from "gsap";
import { PatternModal } from "./components/PatternModal";
import { PatternChooser } from "./components/PatternChooser";
import { useStore } from "./hooks/useStore";
import { CASING_KEYS, CPP_STYLE_KEYS } from "./lib/format";
import "@fontsource/space-grotesk/500.css";
import "@fontsource/space-grotesk/700.css";
import "./index.css";

function BulletDecal({ className = "" }) {
    return (
        <svg className={`bullet ${className}`} viewBox="0 0 100 100">
            <defs>
                <radialGradient id="burn" cx="50%" cy="50%" r="50%">
                    <stop offset="0%" stopColor="#020204" />
                    <stop offset="38%" stopColor="#060608" />
                    <stop offset="62%" stopColor="#251017" />
                    <stop offset="100%" stopColor="rgba(255,45,149,.05)" />
                </radialGradient>

                <filter id="rough">
                    <feTurbulence type="fractalNoise" baseFrequency="0.9" numOctaves="3" />
                    <feDisplacementMap in="SourceGraphic" scale="6" />
                </filter>
            </defs>

            <circle cx="50" cy="50" r="16" fill="url(#burn)" filter="url(#rough)" />
            <circle cx="50" cy="50" r="9" fill="#010103" />

            <path d="M50 12 L54 36 M82 25 L62 42 M87 57 L65 54 M70 86 L58 64 M35 87 L43 64 M11 64 L35 56 M16 27 L38 43 M48 2 L50 34"
                  stroke="rgba(255,255,255,.18)"
                  strokeWidth="2"
                  strokeLinecap="round"
            />

            <circle cx="50" cy="50" r="28" fill="none" stroke="rgba(255,45,149,.28)" strokeWidth="2" />
            <circle className="pulse" cx="50" cy="50" r="22" fill="none" stroke="rgba(255,45,149,.55)" strokeWidth="2" />
        </svg>
    );
}

function BgScene() {
    return (
        <Canvas camera={{ position: [0, 0, 6], fov: 45 }}>
            <color attach="background" args={["#0a0612"]} />
            <Stars radius={70} depth={40} count={1800} factor={4} fade speed={1.5} />
            <Float speed={2} rotationIntensity={1.5} floatIntensity={1}>
                <mesh position={[2.6, -1.4, -1]}>
                    <torusKnotGeometry args={[0.9, 0.18, 128, 16]} />
                    <meshBasicMaterial color="#ff2d95" wireframe transparent opacity={0.16} />
                </mesh>
            </Float>
        </Canvas>
    );
}

export default function App() {
    const shell = useRef(null);
    const rival = useRef(null);
    const { store, error: storeError } = useStore();
    // The open dump + its output options live in the URL (shareable, back/forward
    // via nuqs). casing/style are null until changed, so defaults stay out of the URL.
    const [{ cat, key, casing, style }, setSelection] = useQueryStates({
        cat: parseAsString,
        key: parseAsString,
        casing: parseAsStringEnum([...CASING_KEYS]),
        style: parseAsStringEnum([...CPP_STYLE_KEYS]),
    });
    const [chooserOpen, setChooserOpen] = useState(false);

    useEffect(() => {
        const ctx = gsap.context(() => {
            gsap.from(shell.current, {
                opacity: 0,
                y: 28,
                scale: 0.97,
                duration: 0.8,
                ease: "power3.out",
            });

            gsap.from(".bullet", {
                opacity: 0,
                scale: 0,
                rotate: () => gsap.utils.random(-30, 30),
                stagger: 0.12,
                delay: 0.5,
                duration: 0.22,
                ease: "back.out(2.4)",
            });

            gsap.to(rival.current, {
                x: -7,
                duration: 0.035,
                repeat: 9,
                yoyo: true,
                delay: 0.55,
                ease: "none",
            });

            gsap.fromTo(
                ".strike",
                { scaleX: 0 },
                { scaleX: 1, delay: 1.05, duration: 0.4, ease: "power3.inOut" },
            );
        });

        return () => ctx.revert();
    }, []);

    return (
        <main className="page">
            <div className="three-bg">
                <BgScene />
            </div>

            <section ref={shell} className="shell">
                <div ref={rival} className="rival">
                    <div className="target">
                        <span>cspatterns.dev</span>
                        <i className="strike" />

                        <BulletDecal className="b1" />
                        <BulletDecal className="b2" />
                        <BulletDecal className="b3" />
                    </div>

                    <p>outdated by design</p>
                </div>

                <p className="subtitle">
                    Fastest way to get the latest CS2 patterns and signatures every update.
                </p>

                <nav className="files">
                    <button
                        type="button"
                        className="file file-primary"
                        onClick={() => setChooserOpen(true)}
                    >
                        Patterns
                    </button>
                    <a className="file" href="/tutorials">Tutorials</a>
                </nav>

                <div className="links">
                    <a href="/discord" target="_blank">Discord</a>
                    <a href="/donate">Support</a>
                    <a href="/contact">Contact</a>
                </div>
            </section>

            <PatternChooser
                open={chooserOpen}
                onClose={() => setChooserOpen(false)}
                store={store}
                onPick={(category, entryKey) => {
                    // Reset casing/style so the new language uses its own default.
                    setSelection({ cat: category, key: entryKey, casing: null, style: null });
                    setChooserOpen(false);
                }}
            />

            <PatternModal
                category={cat}
                dumpKey={key}
                casing={casing}
                cppStyle={style}
                store={store}
                storeError={storeError}
                onClose={() => setSelection({ cat: null, key: null, casing: null, style: null })}
                onOption={(patch) =>
                    setSelection({
                        ...(patch.casing !== undefined ? { casing: patch.casing } : {}),
                        ...(patch.cppStyle !== undefined ? { style: patch.cppStyle } : {}),
                    })
                }
            />
        </main>
    );
}
